import { useEffect, useRef, useState } from "react"
import { Button, Space, Tabs, Tour } from "antd"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/pro-solid-svg-icons'
import RoutingContent from "../components/RouteManagement/RoutingContent"
import ScheduleContent, { ScheduledContentRef } from "../components/ScheduleManagement/ScheduleContent"
import { ScheduleHeaderActionsProvider } from "../components/ScheduleManagement/ScheduleHeaderActionsContext"
import ScheduleHeaderActions from "../components/ScheduleManagement/ScheduleHeaderActions"
import { useSavedState } from '../hooks/useSavedState'

const SchedulePage = () => {
  const defaultTab = window.location.hash.includes('/routing') ? '2' : '1'
  const [activeTab, setActiveTab] = useState(defaultTab)
  const [hasShownTour, setHasShownTour] = useSavedState('has-shown-scheduling-tour', false)

  const [isTourOpen, setIsTourOpen] = useState(false)
  const scheduledContentRef = useRef<ScheduledContentRef>(null)

  useEffect(() => {
    const handle = setTimeout(() => {
      if (!hasShownTour) {
        scheduledContentRef.current?.openCreateEventDrawer()
        setIsTourOpen(true)
      }
    }, 1000)

    return () => clearTimeout(handle)
  }, [hasShownTour])

  const handleFeedback = () => {
    window.posthog.displaySurvey('019c067c-aca8-0000-8149-57bc388eff46', {
      ignoreConditions: true,
      ignoreDelay: true,
    })
  }

  const isVisible = (el: HTMLElement | null): el is HTMLElement => {
    return el !== null && el.getClientRects().length > 0
  }

  const titleBarTarget = (id: string) => () => {
    const el = document.getElementById(id) as HTMLElement | null
    if (isVisible(el)) {
      return el
    }
    return document.getElementById('calendar-filter-button') as HTMLElement || null
  }

  const firstVisibleTarget = (...ids: string[]) => () => {
    const visible = ids.map(id => document.getElementById(id)).find(isVisible)
    return visible as HTMLElement || null
  }

  const allSteps = [
    {
      title: 'Add to Schedule',
      description: 'Add a job or on-demand route to your schedule.',
      target: () => document.getElementById('create-event-button') as HTMLElement || null,
    },
    {
      title: 'Scheduling Jobs and Routes',
      description: 'Select a job or drag and drop it onto the calendar to schedule it.',
      target: () => document.getElementById('create-event-drawer') as HTMLElement || null,
      placement: 'left' as const,
    },
    {
      title: 'Switch Calendar Views',
      description: 'Toggle between day, week, and month views.',
      target: firstVisibleTarget('calendar-mode-switcher', 'calendar-mode-select'),
    },
    {
      title: 'Filter by Crew Member',
      description: 'Select which crew members to display on the calendar.',
      target: titleBarTarget('calendar-crew-filter'),
    },
    {
      title: 'Filter by Status',
      description: 'Show or hide events based on their status.',
      target: titleBarTarget('calendar-status-filter'),
    },
    {
      title: 'Additional Filters',
      description: 'Apply more filters to focus on specific events.',
      target: () => document.getElementById('calendar-filter-button') as HTMLElement || null,
    },
    {
      title: 'Route Management',
      description: 'Set up routes to keep your operations organized.',
      target: () => document.getElementById('route-management-tab') as HTMLElement || null,
    }
  ]

  const handleTourClose = () => {
    setIsTourOpen(false)
    setHasShownTour(true)
  }

  return <div style={{ backgroundColor: 'white' }}>
    <ScheduleHeaderActionsProvider>
    <Tabs
      activeKey={activeTab}
      onChange={setActiveTab}
      tabBarStyle={{ padding: '0 12px' }}
      tabBarExtraContent={{
        right: (
          <Space>
            {activeTab === '1' && <ScheduleHeaderActions />}
            <Button type="primary" onClick={handleFeedback}>
              <Space>
                <FontAwesomeIcon icon={faStar} />
                Give Feedback
              </Space>
            </Button>
          </Space>
        ),
      }}
      items={[
        {
          label: <b>Schedule</b>,
          key: '1',
          children: <ScheduleContent ref={scheduledContentRef} />,
        },
        {
          label: <b id="route-management-tab">Route Management</b>,
          key: '2',
          children: <RoutingContent />,
        },
      ]}
    />
    <Tour open={isTourOpen} onClose={handleTourClose} steps={allSteps.filter(step => step.target() !== null)} />
    </ScheduleHeaderActionsProvider>
  </div>
}

export default SchedulePage
